/**
 * The left pane (`060-notebook-tree.md` §5, "Anatomy").
 *
 * Favorites is FLAT and title-sorted, deliberately: a favorites list that reproduces the
 * hierarchy is just a second tree, and a second tree is twice the drag-and-drop surface
 * for no gain. A favorite still appears in its real place in the tree below — it is a
 * shortcut, not a move.
 *
 * The Favorites section is a client-side `filter()` over the tree that is ALREADY loaded —
 * `isFavorite` comes down in `GET /api/pages/tree`. **No extra fetch** (`070` §8). It is
 * hidden entirely when empty, not shown-and-empty.
 */
import { useState } from 'react';
import { CalendarDays, ChevronRight, Settings as SettingsIcon, Sparkles, Star } from 'lucide-react';
import { NavLink } from 'react-router';
import { Trash } from '@/features/notebook/Trash';
import { Tree } from '@/features/notebook/Tree';
import { useTree } from '@/features/notebook/usePageMutations';
import { TagsSection } from '@/features/tags/TagsSection';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';

function Favorites() {
  const [open, setOpen] = useState(true);
  const tree = useTree();
  const { t } = useTranslation();

  const favorites = (tree.data?.pages ?? [])
    .filter((page) => page.isFavorite)
    .sort((a, b) => a.title.localeCompare(b.title));

  if (favorites.length === 0) return null;

  return (
    <div className="shrink-0 pb-1">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          setOpen((previous) => !previous);
        }}
        className="flex h-[var(--row-h)] w-full items-center gap-1.5 px-[var(--pad-x)] text-[var(--muted-foreground)] hover:bg-[var(--accent)]"
      >
        <ChevronRight className={cn('size-3 transition-transform', open && 'rotate-90')} />
        <Star className="size-[var(--icon)]" strokeWidth={1.75} />
        <span className="text-xs font-medium tracking-wide uppercase">
          {t('shell.sidebar.favorites')}
        </span>
      </button>

      {open &&
        favorites.map((page) => (
          <NavLink
            key={page.id}
            to={`/p/${page.id}`}
            className={({ isActive }) =>
              cn(
                'flex h-[var(--row-h)] items-center gap-1 rounded-sm px-[var(--pad-x)] hover:bg-[var(--accent)]',
                isActive && 'bg-[var(--accent)] font-medium',
              )
            }
          >
            <span className="size-4 shrink-0 text-center">{page.icon ?? '★'}</span>
            <span className="truncate">{page.title}</span>
          </NavLink>
        ))}
    </div>
  );
}

/**
 * The calendar's way in. `080` §3 makes the AGENDA the default screen — "the app's answer to
 * what do I have to do and when" — so this points at `/calendar`, not at a grid.
 */
function CalendarLink() {
  const { t } = useTranslation();
  return (
    <div className="shrink-0 pb-1">
      <NavLink
        to="/calendar"
        end
        className={({ isActive }) =>
          cn(
            'flex h-[var(--row-h)] items-center gap-1.5 rounded-sm px-[var(--pad-x)] hover:bg-[var(--accent)]',
            isActive && 'bg-[var(--accent)] font-medium',
          )
        }
      >
        <CalendarDays className="size-[var(--icon)] shrink-0" strokeWidth={1.75} />
        <span className="truncate">{t('shell.sidebar.calendar')}</span>
      </NavLink>
    </div>
  );
}

/**
 * The chat workspace's way in (`090` §10, `111` §7). The slide-over (`Mod+E`) is the surface
 * you use *while reading a page*; this is the one you use to read a conversation back.
 */
function ChatLink() {
  const { t } = useTranslation();
  return (
    <div className="shrink-0 pb-1">
      <NavLink
        to="/chat"
        className={({ isActive }) =>
          cn(
            'flex h-[var(--row-h)] items-center gap-1.5 rounded-sm px-[var(--pad-x)] hover:bg-[var(--accent)]',
            isActive && 'bg-[var(--accent)] font-medium',
          )
        }
      >
        <Sparkles className="size-[var(--icon)] shrink-0" strokeWidth={1.75} />
        <span className="truncate">{t('shell.sidebar.chat')}</span>
      </NavLink>
    </div>
  );
}

/**
 * `100-settings.md` §5: the settings screen is "reachable from the palette (`> Open settings`)
 * and from **the sidebar footer**". This is the footer. It holds one link, and it is not a place
 * for anything else.
 */
function SettingsLink() {
  const { t } = useTranslation();
  return (
    <div className="shrink-0 border-t border-[var(--border)] pt-1">
      <NavLink
        to="/settings"
        className={({ isActive }) =>
          cn(
            'flex h-[var(--row-h)] items-center gap-1.5 rounded-sm px-[var(--pad-x)] text-[var(--muted-foreground)] hover:bg-[var(--accent)]',
            isActive && 'bg-[var(--accent)] font-medium text-[var(--foreground)]',
          )
        }
      >
        <SettingsIcon className="size-[var(--icon)] shrink-0" strokeWidth={1.75} />
        <span className="truncate">{t('shell.sidebar.settings')}</span>
      </NavLink>
    </div>
  );
}

export function Sidebar() {
  const { t } = useTranslation();
  return (
    <nav
      aria-label={t('shell.sidebar.nav')}
      className="flex h-full min-h-0 flex-col overflow-hidden bg-[var(--muted)] py-1"
    >
      <CalendarLink />
      <ChatLink />
      <Favorites />
      <Tree />
      <TagsSection />
      <Trash />
      <SettingsLink />
    </nav>
  );
}
